import React, {
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useReducer,
	useRef,
	useState,
} from "react";

import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	IconButton,
	Tooltip,
	Typography,
	makeStyles
} from "@material-ui/core";
import { blue } from "@material-ui/core/colors";
import {
	AccessTime,
	Block,
	BrandingWatermark,
	Close,
	Done,
	DoneAll,
	ExpandMore,
	Reply
} from "@material-ui/icons";
import ForwardIcon from "@material-ui/icons/Forward";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import Lock from "@material-ui/icons/Lock";
import clsx from "clsx";
import ReactHtmlParser from "react-html-parser";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import { AuthContext } from "../../context/Auth/AuthContext";
import { useBusinessTimezone } from "../../context/BusinessTimezone/BusinessTimezoneContext";
import { ThemeContext } from "../../context/Theme/ThemeContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import apiV2 from "../../services/apiV2";
import { i18n } from "../../translate/i18n";
import { getBackendUrl } from "../../config";
import { resolveMediaUrl } from "../../helpers/resolveMediaUrl";
import {
	connectSocket,
	addGoWebSocketMessageHandler,
	addGoWebSocketOpenHandler,
	getGoWebSocket,
} from "../../services/socketv2";
import openSocket from "../../services/socket-io";
import Audio from "../Audio";
import EmailMessageCard from "../EmailMessageCard";
import { ForwardMessagesModal } from "../ForwardMessagesModal";
import LocationPreview from "../LocationPreview";
import { buildGoogleMapsLink } from "../../helpers/locationMap";
import MarkdownWrapper from "../MarkdownWrapper";
import MessageOptionsMenu from "../MessageOptionsMenu";
import ModalImageCors from "../ModalImageCors";
import TemplateOnlyBar from "../TemplateOnlyBar";
import VcardPreview from "../VcardPreview";
import QuotedMessage from "./components/QuotedMessage";
import CtwaAdCard, { parseCtwaContext } from "./components/CtwaAdCard";
import {
	formatMessageCalendarDay,
	formatMessageClock,
	isSameWallDayInZone,
	toDateFromApi,
} from "../../helpers/dateTimeFormat";
import { ReactionsMsg } from "../reactions";
import MessageReactionBadge from "../reactions/MessageReactionBadge";
import SentimentSatisfiedIcon from '@material-ui/icons/SentimentSatisfied';
import { toast } from "react-toastify";
import { resolveAnnotationAuthorName } from "../../helpers/annotationAuthor";
import { TICKET_ANNOTATION_CREATED } from "../../helpers/ticketAnnotationEvents";

const messageBodyText = (body) => String(body ?? "");

const displayDownloadFileName = (message) => {
	const body = messageBodyText(message?.body);
	if (message?.fromMe && body.includes("_")) {
		return body.split("_").slice(1).join("_") || body;
	}
	return body || "Arquivo";
};

const useStyles = makeStyles((theme) => ({
	messagesListWrapper: {
		overflow: "hidden",
		position: "relative",
		display: "flex",
		flexDirection: "column",
		flexGrow: 1,
	},

	ticketNumber: {
		padding: 8,
		fontSize: "18px",
		fontWeight: "bold",
	},

	messagesList: {
		display: "flex",
		flexDirection: "column",
		flexGrow: 1,
		padding: "20px 20px 20px 20px",
		overflowY: "scroll",

		[theme.breakpoints.down("sm")]: {
			paddingBottom: "90px",
		},
		...theme.scrollbarStyles,
		gap: "25px",
	},

	circleLoading: {
		color: blue[500],
		position: "absolute",
		opacity: "70%",
		top: 0,
		left: "50%",
		marginTop: 12,
	},

	scrollDownButton: {
		position: "absolute",
		bottom: 16,
		right: 16,
		zIndex: 5,
		backgroundColor: "#fff",
		boxShadow: "0 1px 3px #b3b3b3",
	},
	scrollDownButtonDark: {
		backgroundColor: "#202c33",
		color: "#798287",
	},

	messageLeft: {
		marginRight: 20,
		marginTop: 2,
		minWidth: 100,
		maxWidth: 400,
		height: "auto",
		display: "block",
		position: "relative",
		"&:hover #messageActionsButton": {
			display: "flex",
			position: "absolute",
			top: 0,
			right: 0,
		},

		whiteSpace: "pre-wrap",
		backgroundColor: theme.messagesList.messageLeft.backgroundColor,
		color: theme.messagesList.messageLeft.color,
		alignSelf: "flex-start",
		borderTopLeftRadius: 0,
		borderTopRightRadius: 8,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
		paddingLeft: 5,
		paddingRight: 5,
		paddingTop: 5,
		paddingBottom: 0,
		boxShadow: "0 1px 1px #b3b3b3",
		transition: '1s',
	},

	messageLeftDark: {
		marginRight: 20,
		marginTop: 2,
		minWidth: 100,
		maxWidth: 400,
		height: "auto",
		display: "block",
		position: "relative",
		"&:hover #messageActionsButton": {
			display: "flex",
			position: "absolute",
			top: 0,
			right: 0,
		},

		whiteSpace: "pre-wrap",
		backgroundColor: theme.messagesList.messageLeft.backgroundColor,
		color: theme.messagesList.messageLeft.color,
		alignSelf: "flex-start",
		borderTopLeftRadius: 0,
		borderTopRightRadius: 8,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
		paddingLeft: 5,
		paddingRight: 5,
		paddingTop: 5,
		paddingBottom: 0,
		transition: '1s',
	},

	sticker: {
		marginBottom: -25,
		marginLeft: -14,
	},

	stickerMessageLeftDark: {
		marginLeft: 20,
		marginRight: 20,
		marginTop: 2,
		minWidth: 100,
		maxWidth: 200,
		display: "block",
		position: "relative",
		height: 18,
		"&:hover #messageActionsButton": {
			display: "flex",
			position: "absolute",
			top: 0,
			right: 0,
		},

		whiteSpace: "pre-wrap",
		color: theme.messagesList.messageLeft.color,
		alignSelf: "flex-start",
		borderTopLeftRadius: 0,
		borderTopRightRadius: 8,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
		paddingLeft: 5,
		paddingRight: 5,
		paddingTop: 5,
		paddingBottom: 0,
		minHeight: 210,
	},

	stickerMessageLeft: {
		marginLeft: 20,
		marginRight: 20,
		marginTop: 2,
		minWidth: 100,
		maxWidth: 200,
		display: "block",
		position: "relative",
		height: 18,
		"&:hover #messageActionsButton": {
			display: "flex",
			position: "absolute",
			top: 0,
			right: 0,
		},

		whiteSpace: "pre-wrap",
		color: theme.messagesList.messageLeft.color,
		alignSelf: "flex-start",
		borderTopLeftRadius: 0,
		borderTopRightRadius: 8,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
		paddingLeft: 5,
		paddingRight: 5,
		paddingTop: 5,
		paddingBottom: 0,
		minHeight: 210,
	},

	stickerNoBubble: {
		backgroundColor: "transparent !important",
		boxShadow: "none !important",
		border: "none !important",
	},

	stickerTimestamp: {
		padding: "2px 5px",
		borderRadius: 6,
		backgroundColor: theme.messagesList.messageLeft.backgroundColor,
		fontSize: 11,
		position: "absolute",
		bottom: 0,
		right: 5,
		color: "#999",
		boxShadow: "0 1px 1px #b3b3b3",
	},

	quotedMsg: {
		padding: 10,
		maxWidth: 300,
		height: "auto",
		display: "block",
		whiteSpace: "pre-wrap",
		overflow: "hidden",
	},

	quotedSideColorLeft: {
		flex: "none",
		width: "4px",
		backgroundColor: "#6bcbef",
	},

	messageRight: {
		marginLeft: 20,
		marginTop: 2,
		minWidth: 100,
		maxWidth: 400,
		height: "auto",
		display: "block",
		position: "relative",
		"&:hover #messageActionsButton": {
			display: "flex",
			position: "absolute",
			top: 0,
			right: 0,
		},

		whiteSpace: "pre-wrap",
		backgroundColor: theme.messagesList.messageRight.backgroundColor,
		color: theme.messagesList.messageRight.color,
		alignSelf: "flex-end",
		borderTopLeftRadius: 8,
		borderTopRightRadius: 8,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 0,
		paddingLeft: 5,
		paddingRight: 5,
		paddingTop: 5,
		paddingBottom: 0,
		boxShadow: "0 1px 1px #b3b3b3",
		transition: '1s',
	},

	messageRightDark: {
		marginLeft: 20,
		marginTop: 2,
		minWidth: 100,
		maxWidth: 400,
		height: "auto",
		display: "block",
		position: "relative",
		"&:hover #messageActionsButton": {
			display: "flex",
			position: "absolute",
			top: 0,
			right: 0,
		},

		whiteSpace: "pre-wrap",
		backgroundColor: theme.messagesList.messageRight.backgroundColor,
		color: theme.messagesList.messageRight.color,
		alignSelf: "flex-end",
		borderTopLeftRadius: 8,
		borderTopRightRadius: 8,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 0,
		paddingLeft: 5,
		paddingRight: 5,
		paddingTop: 5,
		paddingBottom: 0,
		transition: '1s',
	},

	quotedContainerLeft: {
		margin: "-3px -80px 6px -6px",
		overflow: "hidden",
		backgroundColor: "#f5f6f6",
		borderRadius: "7.5px",
		display: "flex",
		position: "relative",
	},

	quotedContainerLeftDarkMode: {
		margin: "-3px -80px 6px -6px",
		overflow: "hidden",
		backgroundColor: "#1d282f",
		borderRadius: "7.5px",
		display: "flex",
		position: "relative",
	},

	quotedContainerRight: {
		margin: "-3px -80px 6px -6px",
		overflowY: "hidden",
		backgroundColor: "#d1f4cc",
		borderRadius: "7.5px",
		display: "flex",
		position: "relative",
	},

	quotedContainerRightDarkMode: {
		margin: "-3px -80px 6px -6px",
		overflowY: "hidden",
		backgroundColor: "#025144",
		borderRadius: "7.5px",
		display: "flex",
		position: "relative",
	},

	quotedMsgRight: {
		padding: 10,
		maxWidth: 300,
		height: "auto",
		whiteSpace: "pre-wrap",
	},

	quotedSideColorRight: {
		flex: "none",
		width: "4px",
		backgroundColor: "#35cd96",
	},

	messageActionsButton: {
		display: "none",
		position: "relative",
		color: "#999",
		zIndex: 1,
		backgroundColor: "inherit",
		opacity: "90%",
		"&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
	},

	messageContactName: {
		display: "flex",
		color: "#6bcbef",
		fontWeight: 500,
	},

	ackErrorIcon: {
		color: "#f44336",
	},

	annotationRow: {
		width: "100%",
		display: "flex",
		justifyContent: "center",
		padding: "10px 12px",
		margin: "10px 0 14px",
		boxSizing: "border-box",
		clear: "both",
	},
	annotation: {
		display: "flex",
		flexDirection: "column",
		width: "100%",
		maxWidth: "34rem",
		margin: 0,
		backgroundColor: "#fffbeb",
		borderRadius: "8px",
		border: "2px solid #f59e0b",
		borderLeft: "5px solid #f59e0b",
		padding: "10px 14px",
		color: "#78350f",
		boxShadow: "0 2px 14px rgba(245, 158, 11, 0.28)",
		"& p": {
			padding: "0",
			margin: "0",
		},
	},
	annotationHeader: {
		display: "flex",
		alignItems: "center",
		gap: "6px",
		marginBottom: "8px",
		paddingBottom: "8px",
		borderBottom: "1px dashed rgba(180, 83, 9, 0.4)",
		fontSize: "0.8125rem",
		fontWeight: 600,
		color: "#b45309",
	},
	annotationMeta: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-end",
		gap: "0.3em",
		marginTop: "8px",
		fontSize: "12px",
		color: "#b45309",
	},

	textContentItem: {
		overflowWrap: "break-word",
		padding: "3px 80px 6px 6px",
	},

	emailContentItem: {
		display: "flex",
		flexDirection: "column",
	},

	textContentItemDeleted: {
		fontStyle: "italic",
		color: "rgba(0, 0, 0, 0.36)",
		overflowWrap: "break-word",
		padding: "3px 80px 6px 6px",
	},

	messageMedia: {
		// objectFit: "cover",
		width: "100%",
		// height: 200,
		borderTopLeftRadius: 8,
		borderTopRightRadius: 8,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
	},

	timestamp: {
		fontSize: 11,
		position: "absolute",
		bottom: 0,
		right: 5,
		color: "#999",
	},

	dailyTimestamp: {
		alignItems: "center",
		textAlign: "center",
		alignSelf: "center",
		width: "110px",
		backgroundColor: "#e1f3fb",
		margin: "10px",
		borderRadius: "10px",
		boxShadow: "0 1px 1px #b3b3b3",
	},

	dailyTimestampText: {
		color: "#808888",
		padding: 8,
		alignSelf: "center",
		marginLeft: "0px",
	},

	ackIcons: {
		fontSize: 18,
		verticalAlign: "middle",
		marginLeft: 4,
	},

	deletedIcon: {
		fontSize: 18,
		verticalAlign: "middle",
		marginRight: 4,
	},

	ackDoneAllIcon: {
		fill: blue[500],
		fontSize: 18,
		verticalAlign: "middle",
		marginLeft: 4,
	},

	downloadMedia: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		// backgroundColor: "#F5F6F6",
		backgroundColor: "rgba(0,0,0, 0.03)",
		borderRadius: "5px",
		padding: 10,
		gap: 5,
	},

	downloadButton: {
		transform: "rotate(90deg)",
		border: "1px solid #79909B",
		padding: 6,
		marginLeft: 10,
	},

	downloadFileNameFull: {
		width: "100%",
		fontSize: 15,
		whiteSpace: "wrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		wordBreak: "break-all",
	},

	downloadFileName: {
		fontSize: 15,
		display: "-webkit-box",
		boxOrient: "vertical",
		lineClamp: 2,
		wordBreak: "break-all",
		overflow: "hidden",
	},

	downloadExtraInfo: {
		fontSize: 11,
		color: "#79909B",
		opacity: "0.7",
	},

	messageCenter: {
		marginTop: 5,
		alignItems: "center",
		verticalAlign: "center",
		alignContent: "center",
		backgroundColor: "#E1F5FEEB",
		fontSize: "12px",
		minWidth: 100,
		maxWidth: 270,
		color: "#272727",
		borderTopLeftRadius: 0,
		borderTopRightRadius: 8,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
		paddingLeft: 5,
		paddingRight: 5,
		paddingTop: 5,
		paddingBottom: 0,
		boxShadow: "0 1px 1px #b3b3b3",
	},

	emojiBox: {
		backgroundColor: "#fafafa",
		position: "absolute",
		right: 10,
		marginTop: 3,
		borderRadius: "50%",
		padding: "2px 4px",
		boxShadow: "0 1px 3px #b3b3b3",
		zIndex: 1,
	},

	darkEmojiBox: {
		backgroundColor: "#202c33",
		position: "absolute",
		right: 10,
		marginTop: 3,
		borderRadius: "50%",
		padding: "2px 4px",
		boxShadow: "0 1px 3px #202c33",
		zIndex: 1,
	},

	forwardMenu: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: "0 20px",
		backgroundColor: "#F0F2F5",
		position: "absolute",
		bottom: 0,
		marginTop: "30px",
		width: "100%",
		zIndex: 2,

		// svg icons color
		"& svg": {
			color: "#54656F",
		},
	},
}));

const messageTimestamp = (message) => {
	if (!message?.createdAt) return Number.MAX_SAFE_INTEGER;
	const date =
		message.createdAt instanceof Date
			? message.createdAt
			: new Date(message.createdAt);
	const time = date.getTime();
	return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
};

const normalizeIncomingMessage = (message, { defaultToNow = false } = {}) => {
	if (!message) return message;
	if (message.createdAt != null && message.createdAt !== "") return message;
	if (defaultToNow) {
		return { ...message, createdAt: new Date().toISOString() };
	}
	return message;
};

const resolveMessageTicketId = (message) =>
	message?.ticketId ?? message?.ticket?.id ?? null;

const isAnnotationMessage = (message) =>
	String(message?.mediaType || "").toLowerCase() === "annotation";

const isStatusChannelMessage = (message) =>
	message?.isStatus === true ||
	message?.isStatus === 1 ||
	message?.isStatus === "1";

const messageBelongsToOpenTicketView = (message, openTicketId, ticketIsStatus) => {
	if (!message) return false;
	if (isAnnotationMessage(message)) {
		const messageTicketId = resolveMessageTicketId(message);
		if (messageTicketId == null) {
			return true;
		}
		return String(messageTicketId) === String(openTicketId);
	}
	if (ticketIsStatus) {
		return isStatusChannelMessage(message);
	}
	return !isStatusChannelMessage(message);
};

const filterMessagesForOpenTicket = (messages, openTicketId, ticketIsStatus) =>
	(messages || []).filter((message) => {
		if (isAnnotationMessage(message)) {
			return false;
		}
		return messageBelongsToOpenTicketView(message, openTicketId, ticketIsStatus);
	});

const compareMessagesByCreatedAt = (a, b) => {
	const timeDiff = messageTimestamp(a) - messageTimestamp(b);
	if (timeDiff !== 0) return timeDiff;
	return String(a.id).localeCompare(String(b.id));
};

const sortMessagesByCreatedAt = (messages) =>
	[...messages].sort(compareMessagesByCreatedAt);

const mergeMessages = (current, incoming) => {
	const byId = new Map(current.map((message) => [message.id, message]));
	incoming.forEach((rawMessage) => {
		const message = normalizeIncomingMessage(rawMessage)
		const existing = byId.get(message.id);
		byId.set(
			message.id,
			existing ? { ...existing, ...message } : message
		);
	});
	return sortMessagesByCreatedAt(Array.from(byId.values()));
};

const reducer = (state, action) => {
	if (action.type === "LOAD_MESSAGES") {
		return mergeMessages(state, action.payload);
	}

	if (action.type === "ADD_MESSAGE") {
		return mergeMessages(state, [
			normalizeIncomingMessage(action.payload, { defaultToNow: true }),
		]);
	}

	if (action.type === "UPDATE_MESSAGE") {
		const incoming = { ...action.payload };
		const lookupId =
			action.tempId != null && action.tempId !== ""
				? action.tempId
				: incoming.id;

		const sameMessageId = (a, b) => {
			if (a == null || b == null) return false;
			const sa = String(a);
			const sb = String(b);
			if (sa === sb) return true;
			// id com/sem sufixo "-companyId"
			const base = (id) => {
				const s = String(id);
				const i = s.lastIndexOf("-");
				return i > 0 ? s.slice(0, i) : s;
			};
			return base(sa) === base(sb) || base(sa) === sb || sa === base(sb);
		};

		const messageIndex = state.findIndex(
			(m) => sameMessageId(m.id, lookupId) || sameMessageId(m.id, incoming.id)
		);

		if (messageIndex === -1) {
			return state;
		}

		const merged = {
			...state[messageIndex],
			...incoming,
			// troca o id temporário pelo id real do Hub/WhatsApp
			id: incoming.id || state[messageIndex].id,
		};
		if (incoming.ack !== undefined && incoming.ack !== null) {
			merged.ack = Number(incoming.ack);
		}
		if (incoming.isDeleted === true || incoming.isDeleted === 1 || incoming.isDeleted === "1") {
			merged.isDeleted = true;
		}
		if (incoming.isEdited === true || incoming.isEdited === 1 || incoming.isEdited === "1") {
			merged.isEdited = true;
		}

		const next = [...state];
		next[messageIndex] = merged;
		return sortMessagesByCreatedAt(next);
	}

	if (action.type === "RESET") {
		return [];
	}

	return state;
};

const OFFICIAL_WHATSAPP_CHANNELS = new Set([
	"whatsappbusiness",
	"whatsapp_business_account",
	"whatsappcloud",
]);
const META_WINDOW_MS = 24 * 60 * 60 * 1000;

function isOfficialWhatsappChannel(channel) {
	return OFFICIAL_WHATSAPP_CHANNELS.has(String(channel || ""));
}

function isOutsideMetaWindow(messages, timeZone) {
	let latestInbound = 0;
	for (const message of messages) {
		if (!message || message.fromMe || isAnnotationMessage(message)) continue;
		if (String(message.mediaType || "").toLowerCase() === "call_log") continue;
		const createdAt = toDateFromApi(message.createdAt, timeZone)?.getTime() ?? 0;
		if (createdAt > latestInbound) latestInbound = createdAt;
	}
	if (!latestInbound) return true;
	return Date.now() - latestInbound >= META_WINDOW_MS;
}

function MessagesList({
	contact,
	ticketId,
	isGroup,
	ticketIsStatus = false,
	ticketChannel,
	connectionChannel,
	templateBarStatus,
	setTemplateBarStatus,
	aroundMessages,
	setAroundMessages,
	whatsappId,
	searchedMessage,
	setSearchLoading,
	setSearchedMessage,
	searchLoading,
	tailwickLayout = false,
	privateAnnotationsOnly = false,
	setMessagePrivate,
	// ref,
}) {
	const classes = useStyles();
	const lastMessageRef = useRef();
	const currentTicketId = useRef(ticketId);
	const suppressLoadMoreUntilRef = useRef(0);
	const loadingMoreRef = useRef(false);
	const scrollPreserveRef = useRef(null);
	const aroundModeRef = useRef(false);
	const topSentinelRef = useRef(null);
	// Na abertura / preenchimento da tela: sempre ancorar no final, não no topo.
	const pinToBottomAfterLoadRef = useRef(true);
	// Limite de páginas carregadas só para preencher a viewport (evita ir até page 20 vazia).
	const autoFillPagesRef = useRef(0);
	const MAX_AUTO_FILL_PAGES = 3;
	const hasMoreRef = useRef(false);
	const { user } = useContext(AuthContext);
	const { darkMode } = useContext(ThemeContext);
	const { timeZone: businessTimeZone } = useBusinessTimezone();

	const [messagesList, dispatch] = useReducer(reducer, []);
	const [pageNumber, setPageNumber] = useState(1);
	const [hasMore, setHasMore] = useState(false);
	const [loading, setLoading] = useState(false);
	const [buttonScrollDown, setButtonScrollDown] = useState(false);
	const [selectedMessage, setSelectedMessage] = useState({});
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedMessages, setSelectedMessages] = useState([]);
	const [forwardModalOpen, setForwardModalOpen] = useState(false);
	const messageOptionsMenuOpen = Boolean(anchorEl);

	const messageBelongsToTicketChat = useCallback(
		(message) =>
			messageBelongsToOpenTicketView(message, ticketId, ticketIsStatus),
		[ticketIsStatus, ticketId]
	);

	const chatMessages = useMemo(() => {
		if (privateAnnotationsOnly) {
			return sortMessagesByCreatedAt(
				(messagesList || []).filter((message) => isAnnotationMessage(message))
			);
		}
		return filterMessagesForOpenTicket(messagesList, ticketId, ticketIsStatus);
	}, [messagesList, ticketId, ticketIsStatus, privateAnnotationsOnly]);

	const [reactionMessageId, setReactionMessageId] = useState(null);

	const handleReaction = (messageId) => {
		setReactionMessageId(prev => prev === messageId ? null : messageId);
	};

	const ReagirMsg = async (messageId, emoji) => {
		if (!messageId) {
			return toast.error("Mensagem não encontrada");
		}

		const removing = !emoji || !String(emoji).trim();

		try {
			if (
				ticketChannel === "whatsappbusiness" ||
				ticketChannel === "whatsapp_business_account"
			) {
				// await api.post("/WhatsAppMeta/send-reaction", {
				// 	messageId,
				// 	emoji: removing ? "" : emoji,
				// 	contact,
				// 	whatsappId,
				// 	ticketId,
				// });

				await apiV2.post(`/hub/sendReaction/${ticketId}`,
					{
						message_id: messageId,
						emoji: removing ? "" : emoji
					})

			} else {
				await apiV2.post(`/message/${ticketId}`, {
					messageId,
					emoji: removing ? "" : emoji,
				});
			}

			dispatch({
				type: "UPDATE_MESSAGE",
				payload: { id: messageId, emoji: removing ? null : emoji },
			});
		} catch (e) {
			toastError(e);
		}
	};

	const renderInlineMessageReaction = (message) => {
		if (!message.emoji || tailwickLayout) return null;
		return (
			<Box
				className={!darkMode ? classes.emojiBox : classes.darkEmojiBox}
				onClick={() => ReagirMsg(message.id, "")}
				style={{ cursor: "pointer" }}
				title="Remover reação"
			>
				{message.emoji}
			</Box>
		);
	};

	const renderBubbleMessageReaction = (message) => {
		if (!message.emoji || !tailwickLayout) return null;
		return (
			<MessageReactionBadge
				emoji={message.emoji}
				onRemove={() => ReagirMsg(message.id, "")}
			/>
		);
	};

	const renderTailwickReactionActions = (message) => {
		if (!tailwickLayout) return null;

		return (
			<div className="tailwick-incoming-message-actions">
				<button
					type="button"
					className="tailwick-message-reaction-btn btn btn-icon size-8 bg-default-150 text-default-500 hover:bg-default-200 hover:text-primary"
					onClick={() => handleReaction(message.id)}
					aria-label="Reagir"
				>
					<svg
						className="size-4"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						aria-hidden
					>
						<circle cx="12" cy="12" r="10" />
						<path d="M8 14s1.5 2 4 2 4-2 4-2" />
						<line x1="9" y1="9" x2="9.01" y2="9" />
						<line x1="15" y1="9" x2="15.01" y2="9" />
					</svg>
				</button>
				{reactionMessageId === message.id && (
					<ReactionsMsg
						tailwickLayout
						activeEmoji={message.emoji}
						onSelect={(emoji) => {
							ReagirMsg(message.id, emoji);
							setReactionMessageId(null);
						}}
					/>
				)}
			</div>
		);
	};

	const getAroundMessages = async (message) => {
		try {
			setSearchLoading(true);
			const {
				data: {
					messages: { messages },
				},
			} = await api.get(`messages/around/${contact.id}/${message.createdAt}`);
			setAroundMessages(messages);
			setSearchedMessage(message);
		} catch (error) {
			console.log(error);
		}
	};


	const pinMessagesListToBottom = useCallback(() => {
		const container = document.getElementById("messagesList");
		if (!container) return;
		container.scrollTop = container.scrollHeight;
		setButtonScrollDown(false);
	}, []);

	const scrollToBottom = (behavior) => {
		if (behavior === "instant") {
			pinToBottomAfterLoadRef.current = true;
			// Pin imediato + retries: o DOM ainda pode estar medindo altura 0
			// no frame do dispatch.
			pinMessagesListToBottom();
			requestAnimationFrame(() => {
				pinMessagesListToBottom();
				requestAnimationFrame(pinMessagesListToBottom);
			});
			window.setTimeout(pinMessagesListToBottom, 50);
			window.setTimeout(pinMessagesListToBottom, 150);
			window.setTimeout(pinMessagesListToBottom, 350);
			return;
		}
		setTimeout(() => {
			pinMessagesListToBottom();
			if (lastMessageRef.current) {
				lastMessageRef.current.scrollIntoView({ behavior });
			}
			setButtonScrollDown(false);
		}, 100);
	};

	// Depois do paint: se ainda estamos na abertura, força o final.
	useLayoutEffect(() => {
		if (!pinToBottomAfterLoadRef.current) return;
		if (aroundModeRef.current || aroundMessages?.length) return;
		if (!chatMessages.length) return;
		pinMessagesListToBottom();
	}, [chatMessages.length, ticketId, pageNumber, aroundMessages, pinMessagesListToBottom]);

	const scrollToMessage = useCallback(() => {
		if (!searchedMessage) return;

		const isMessageFromMe = searchedMessage.fromMe;
		const target = document.getElementById(searchedMessage.id);
		const fromMeColor = darkMode ? '#005c4b' : '#d9fdd3';
		const notFromMeColor = darkMode ? '#202c33' : '#FFFFFF';

		if (target) {
			target.scrollIntoView({ behavior: 'smooth' });
			target.style.backgroundColor = '#d9fdd350';

			setTimeout(() => {
				target.style.backgroundColor = isMessageFromMe ? fromMeColor : notFromMeColor;
			}, 3000);
			setSearchLoading(false);
			setSearchedMessage(null);
		}
	}, [setPageNumber, searchedMessage, hasMore, messagesList]);


	const loadMore = useCallback(() => {
		if (
			loadingMoreRef.current ||
			aroundModeRef.current ||
			aroundMessages?.length
		) {
			return;
		}
		if (Date.now() < suppressLoadMoreUntilRef.current) {
			return;
		}

		const container = document.getElementById("messagesList");
		if (container) {
			const canScroll = container.scrollHeight > container.clientHeight + 8;
			if (canScroll) {
				// Usuário subiu no histórico: preserva o ponto de leitura.
				scrollPreserveRef.current = {
					height: container.scrollHeight,
					top: container.scrollTop,
				};
				pinToBottomAfterLoadRef.current = false;
			} else {
				// Ainda preenchendo a tela na abertura: depois ancora no final.
				scrollPreserveRef.current = null;
				pinToBottomAfterLoadRef.current = true;
			}
		}

		loadingMoreRef.current = true;
		setLoading(true);
		setPageNumber((prevPageNumber) => prevPageNumber + 1);
	}, [aroundMessages]);

	const maybeLoadOlderIfNeeded = useCallback(() => {
		if (aroundModeRef.current || aroundMessages?.length) return;
		if (!hasMore || loadingMoreRef.current) return;
		if (Date.now() < suppressLoadMoreUntilRef.current) return;
		if (autoFillPagesRef.current >= MAX_AUTO_FILL_PAGES) return;

		const container = document.getElementById("messagesList");
		if (!container) return;

		// Só auto-paginar quando NÃO há overflow. Nunca por "nearTop" —
		// na abertura o scroll ainda está no topo antes do pin no final.
		const canScroll = container.scrollHeight > container.clientHeight + 8;
		if (!canScroll) {
			autoFillPagesRef.current += 1;
			loadMore();
		}
	}, [hasMore, aroundMessages, loadMore]);


	const handleScroll = (e) => {
		const scrollBottom =
			e.currentTarget.scrollHeight -
			e.currentTarget.scrollTop -
			e.currentTarget.clientHeight;

		// Usuário saiu do final: para de reancorar no bottom.
		if (scrollBottom > 80) {
			pinToBottomAfterLoadRef.current = false;
		}

		if (scrollBottom > 50 && !buttonScrollDown) {
			setButtonScrollDown(true);
		} else if (scrollBottom <= 50 && buttonScrollDown) {
			setButtonScrollDown(false);
		}

		// loadMore só no IntersectionObserver do topo (evita página dupla).
	};

	const handleOpenMessageOptionsMenu = (e, message) => {
		setAnchorEl(e.currentTarget);
		setSelectedMessage(message);
	};

	const handleCloseMessageOptionsMenu = (e) => {
		setAnchorEl(null);
	};

	const parseMessageMetadata = (raw) => {
		let value = raw;
		for (let i = 0; i < 3; i += 1) {
			if (value == null) return null;
			if (typeof value === "object" && !Array.isArray(value)) return value;
			if (typeof value !== "string") return null;
			const trimmed = value.trim();
			if (!trimmed) return null;
			try {
				value = JSON.parse(trimmed);
			} catch {
				return null;
			}
		}
		if (value && typeof value === "object" && !Array.isArray(value)) {
			return value;
		}
		return null;
	};

	const coordsFromLocationBody = (body) => {
		const text = String(body || "");
		const match = text.match(
			/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/
		);
		if (!match) return null;
		const latitude = Number(match[1]);
		const longitude = Number(match[2]);
		if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
		return { latitude, longitude };
	};

	const checkMessageMedia = (message) => {
		if (message.mediaType === "location" && message.metadata) {
			try {
				const metadata = parseMessageMetadata(message.metadata);
				const location =
					metadata?.location ||
					metadata?._data?.location ||
					coordsFromLocationBody(message.body);

				if (!location) return null;

				const latitude = Number(location.latitude ?? location.lat);
				const longitude = Number(
					location.longitude ?? location.lng ?? location.lon
				);
				if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
					return null;
				}

				const description =
					location.description ||
					location.name ||
					location.address ||
					"";
				const imageLocation =
					metadata?.body ||
					metadata?._data?.body ||
					"";
				const clientUrl =
					metadata?._data?.clientUrl ||
					metadata?.clientUrl ||
					undefined;

				const linkLocation = buildGoogleMapsLink(latitude, longitude);

				return (
					<LocationPreview
						latitude={latitude}
						longitude={longitude}
						image={imageLocation}
						link={linkLocation}
						description={description}
						clientUrl={clientUrl}
					/>
				);
			} catch (err) {
				console.error("Erro ao renderizar localização:", err);
				const fallback = coordsFromLocationBody(message.body);
				if (!fallback) return null;
				return (
					<LocationPreview
						latitude={fallback.latitude}
						longitude={fallback.longitude}
						link={buildGoogleMapsLink(fallback.latitude, fallback.longitude)}
						description=""
					/>
				);
			}
		}

		if (
			message.mediaType === "location" &&
			!message.metadata
		) {
			const fallback = coordsFromLocationBody(message.body);
			if (!fallback) return null;
			return (
				<LocationPreview
					latitude={fallback.latitude}
					longitude={fallback.longitude}
					link={buildGoogleMapsLink(fallback.latitude, fallback.longitude)}
					description=""
				/>
			);
		}

		if (message.mediaType === "vcard" && message.body) {
			const array = messageBodyText(message.body).split("\n");
			const obj = [];
			let contact = "";
			for (let index = 0; index < array.length; index++) {
				const v = array[index];
				const values = v.split(":");
				for (let ind = 0; ind < values.length; ind++) {
					if (values[ind].indexOf("+") !== -1) {
						obj.push({ number: values[ind] });
					}
					if (values[ind].indexOf("FN") !== -1) {
						contact = values[ind + 1];
					}
				}
			}
			return (
				<VcardPreview
					contact={contact}
					numbers={obj[0]?.number}
					mediaType={message.mediaType}
				/>
			);
		}
		if (message.mediaType === "multi_vcard" && message.body) {
			const array = messageBodyText(message.body)
				.replace("[{", "")
				.replace("}]", "")
				.split("},{");

			// array format: ['"id":66,"name":"Nathalia","number":"5527988882406"', '"id":12,"name":"Mãe Trabalho","number":"5527999996918"', '"id":72,"name":"Mariah Irmã","number":"5527997420098"']

			const contactsArray = array.map((item) => {
				const obj = {};
				item.split(",").forEach((i) => {
					const [key, value] = i.split(":");
					obj[key.replace('"', "").replace('"', "")] = value
						?.replace('"', "")
						.replace('"', "");
				});
				return obj;
			});

			return (
				<>
					{/*  {contactsArray.map((contact) => {
						const { name, number } = contact;
						// format number from 5527999999999 to +55 27 99999-9999
						if (!number) return;
						const formattedNumber = `+${number.slice(0, 2)} ${number.slice(
							2,
							4
						)} ${number.slice(4, 9)}-${number.slice(9, 13)}`;

						return (
							<VcardPreview
								contact={name}
								numbers={formattedNumber}
								mediaType={message.mediaType}
							/>
						);
					})} */}
					<VcardPreview
						contacts={contactsArray}
						mediaType={message.mediaType}
					/>
				</>
			);
		}

		if (message.mediaType === "image") {
			if (parseCtwaContext(message.ctwaContext)) {
				return (
					<>
						<CtwaAdCard
							ctwaContext={message.ctwaContext}
							imageUrl={message.mediaUrl}
							darkMode={darkMode}
						/>
						<MarkdownWrapper>{message.body}</MarkdownWrapper>
					</>
				);
			} else {
				return (
					<>
						<ModalImageCors imageUrl={message.mediaUrl} widthProp={400} />
						<MarkdownWrapper>{message.body}</MarkdownWrapper>
					</>
				);
			}

		}
		if (message.mediaType === "audio") {
			return (
				<Audio
					url={resolveMediaUrl(message.mediaUrl)}
					title="Áudio"
					ticketId={message.ticketId ?? ticketId}
				/>
			);
		}
		if (message.mediaType === "video") {
			return (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "10px",
						paddingBottom: "5px",
					}}
				>
					<video
						className={classes.messageMedia}
						src={resolveMediaUrl(message.mediaUrl)}
						controls
					/>
					{message.body && <MarkdownWrapper>{message.body}</MarkdownWrapper>}
				</Box>
			);
		}

		if (message.mediaType === "sticker") {
			return (
				<Box className={classes.sticker}>
					<ModalImageCors
						imageUrl={message.mediaUrl}
						widthProp={"190px"}
						heightProp={"190px"}
					/>
				</Box>
			);
		}

		if (message.mediaType !== "email") {
			return (
				<>
					<div className={classes.downloadMedia}>
						<InsertDriveFileIcon
							style={{ color: "#79909B", fontSize: "2.2rem" }}
						/>
						<Box>
							<Tooltip title={messageBodyText(message.body)}>
								<Typography className={classes.downloadFileName}>
									{displayDownloadFileName(message)}
								</Typography>
							</Tooltip>

							<Box className={classes.downloadExtraInfo}>
								{/* file extension and file size */}
								{`${getFileExtension(message.body)} • ${getFileSize(
									message.mediaUrl
								)}`}
							</Box>
						</Box>

						<IconButton
							className={classes.downloadButton}
							target="_blank"
							rel="noopener noreferrer"
							component="a"
							href={resolveFileMediaUrl(message.mediaUrl)}
						>
							<ForwardIcon
								style={{
									color: "#79909B",
									fontSize: "1rem",
									transform: "scaleX(1.2)",
								}}
							/>
						</IconButton>
					</div>
					<Tooltip title={messageBodyText(message.body)}>
						<Typography
							className={classes.downloadFileNameFull}
							style={{ marginLeft: "0.5px" }}
						>
							{displayDownloadFileName(message)}
						</Typography>
					</Tooltip>
				</>
			);
		}
	};

	const renderMessageAck = (message) => {
		if (!message.fromMe) {
			return null;
		}
		const ack = Number(message.ack);

		if (Number.isNaN(ack) || ack <= 0) {
			return <AccessTime fontSize="small" className={classes.ackIcons} />;
		}
		if (ack === 1) {
			return <Done fontSize="small" className={classes.ackIcons} />;
		}
		if (ack === 2) {
			return <DoneAll fontSize="small" className={classes.ackIcons} />;
		}
		if (ack == 3) {
			return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
		}
		if (ack >= 4) {
			return (
				<ErrorOutlineIcon
					fontSize="small"
					className={classes.ackErrorIcon}
				/>
			);
		}
	};

	const renderDailyTimestamps = (message, index) => {
		if (index === 0) {
			return (
				<span
					className={classes.dailyTimestamp}
					key={`timestamp-${message.id}`}
				>
					<div className={classes.dailyTimestampText}>
						{formatMessageCalendarDay(chatMessages[index].createdAt, businessTimeZone)}
					</div>
				</span>
			);
		}
		if (index < chatMessages.length - 1) {
			if (
				!isSameWallDayInZone(
					chatMessages[index].createdAt,
					chatMessages[index - 1].createdAt,
					businessTimeZone
				)
			) {
				return (
					<span
						className={classes.dailyTimestamp}
						key={`timestamp-${message.id}`}
					>
						<div className={classes.dailyTimestampText}>
							{formatMessageCalendarDay(chatMessages[index].createdAt, businessTimeZone)}
						</div>
					</span>
				);
			}
		}
		if (index === chatMessages.length - 1) {
			return (
				<div
					key={`ref-${message.createdAt}`}
					ref={lastMessageRef}
					style={{ float: "left", clear: "both" }}
				/>
			);
		}
	};

	const getPreviousChatTicketId = (index) => {
		if (privateAnnotationsOnly) {
			if (index < 1) return null;
			return chatMessages[index - 1]?.ticketId ?? null;
		}
		for (let i = index - 1; i >= 0; i -= 1) {
			const previous = chatMessages[i];
			if (isAnnotationMessage(previous)) continue;
			return previous?.ticketId;
		}
		return null;
	};

	const renderAnnotationMessage = (message, index) => {
		const authorName =
			resolveAnnotationAuthorName(message, "") ||
			user?.name ||
			user?.email ||
			i18n.t("messagesInput.privateMessageUnknownAuthor");

		return (
			<React.Fragment key={message.id}>
				{renderDailyTimestamps(message, index)}
				{renderMessageDivider(message, index)}
				{renderNumberTicket(message, index)}
				<Box
					display="flex"
					alignItems="center"
					width="100%"
					justifyContent="flex-end"
					className={
						tailwickLayout ? "tailwick-outgoing-message-row" : undefined
					}
				>
					<div
						className={clsx(
							darkMode ? classes.messageRightDark : classes.messageRight,
							tailwickLayout && "tailwick-chat-annotation-bubble"
						)}
						id={message.id}
						data-message-type="private"
						role="note"
						aria-label={i18n.t("messagesInput.privateMessageAlertTitle")}
					>
						<span className={classes.messageContactName}>{authorName}</span>
						<div className={classes.textContentItem}>
							{ReactHtmlParser(message.body)}
							<span className={classes.timestamp}>
								<Lock fontSize="inherit" aria-hidden />
								{formatMessageClock(message.createdAt, businessTimeZone)}
							</span>
						</div>
					</div>
				</Box>
			</React.Fragment>
		);
	};

	const renderNumberTicket = (message, index) => {
		if (isAnnotationMessage(message) && !privateAnnotationsOnly) {
			return null;
		}
		if (index < 1) {
			return null;
		}

		const previousTicketId = getPreviousChatTicketId(index);
		if (
			previousTicketId != null &&
			String(message.ticketId) !== String(previousTicketId)
		) {
			return (
				<div key={`ticket-${message.id}`} className={classes.ticketNumber}>
					Ticket #{message.ticketId}
					<hr />
				</div>
			);
		}
	};

	const renderMessageDivider = (message, index) => {
		if (index < chatMessages.length && index > 0) {
			const messageUser = chatMessages[index].fromMe;
			const previousMessageUser = chatMessages[index - 1].fromMe;

			if (messageUser !== previousMessageUser) {
				return <span style={{ marginTop: 16 }} key={`divider-${message.id}`} />;
			}
		}
	};

	const sortByKey = (array, key) => {
		return array.sort((a, b) => {
			const x = a[key];
			const y = b[key];
			return x < y ? -1 : x > y ? 1 : 0;
		});
	};

	const handleSelectedMessages = (messageId) => {
		if (selectedMessages.includes(messageId)) {
			setSelectedMessages(
				selectedMessages.filter((message) => message !== messageId)
			);
		} else {
			setSelectedMessages([...selectedMessages, messageId]);
		}
	};

	const renderMessages = () => {
		if (chatMessages.length > 0) {
			const viewMessagesList = chatMessages.map((message, index) => {
				if (isAnnotationMessage(message)) {
					return renderAnnotationMessage(message, index);
				}

				if (message.mediaType === "call_log") {
					return (
						<React.Fragment key={message.id}>
							{renderDailyTimestamps(message, index)}
							{renderMessageDivider(message, index)}
							{renderNumberTicket(message, index)}
							<div
								className={classes.messageCenter}
								id={message.id}
							>
								<IconButton
									variant="contained"
									size="small"
									id="messageActionsButton"
									disabled={message.isDeleted}
									className={classes.messageActionsButton}
									onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
								>
									<ExpandMore />
								</IconButton>

								{isGroup && (
									<span className={classes.messageContactName}>
										{message.contact?.name}
									</span>
								)}

								<div>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 20 17"
										width="20"
										height="17"
									>
										<path
											fill="#df3333"
											d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"
										/>
									</svg>{" "}
									<span>
										Chamada de voz/vídeo perdida às{" "}
										{formatMessageClock(message.createdAt, businessTimeZone)}
									</span>
								</div>
							</div>
						</React.Fragment>
					);
				}

				if (!message.fromMe) {
					return (
						<React.Fragment key={message.id}>
							{renderDailyTimestamps(message, index)}
							{renderMessageDivider(message, index)}
							{renderNumberTicket(message, index)}
							<Box
								display="flex"
								alignItems={tailwickLayout ? "flex-end" : "center"}
								position="relative"
								className={tailwickLayout ? "tailwick-incoming-message-row" : undefined}
							>
								{selectedMessages.length > 0 ? (
									<Checkbox
										onClick={() => handleSelectedMessages(message.id)}
										checked={selectedMessages.includes(message.id)}
									/>
								) : (
									<span></span>
								)}

								{message.mediaType === "sticker" ? (
									<>
										<div
											className={[
												darkMode
													? classes.stickerMessageLeftDark
													: classes.stickerMessageLeft,
											]}
											id={message.id}
											style={{ position: "relative" }}
										>
											<IconButton
												variant="contained"
												size="small"
												id="messageActionsButton"
												disabled={message.isDeleted}
												className={classes.messageActionsButton}
												onClick={(e) =>
													handleOpenMessageOptionsMenu(e, message)
												}
											>
												<ExpandMore />
											</IconButton>
											<Box className={classes.sticker}>
												<ModalImageCors
													imageUrl={message.mediaUrl}
													widthProp={"190px"}
													heightProp={"190px"}
												/>
											</Box>
											<span className={classes.stickerTimestamp}>
												{formatMessageClock(message.createdAt, businessTimeZone)}
											</span>
											{renderBubbleMessageReaction(message)}
										</div>
										{renderTailwickReactionActions(message)}
									</>
								) : (
									<div
										className={[
											darkMode
												? classes.messageLeftDark
												: classes.messageLeft,
										]}
										style={
											message.mediaType === "email"
												? {
													padding: "0.5em 0 0 ",
													margin: "0.5em 0 0",
												}
												: null
										}
										id={message.id}
									>
										{message.mediaType !== "email" && (
											<IconButton
												variant="contained"
												size="small"
												id="messageActionsButton"
												disabled={message.isDeleted}
												className={classes.messageActionsButton}
												onClick={(e) =>
													handleOpenMessageOptionsMenu(e, message)
												}
											>
												<ExpandMore />
											</IconButton>
										)}
										{isGroup && (
											<span className={classes.messageContactName}>
												{message.contact?.name}
											</span>
										)}
										{message.mediaType !== "image" &&
											parseCtwaContext(message.ctwaContext) && (
												<CtwaAdCard
													ctwaContext={message.ctwaContext}
													darkMode={darkMode}
												/>
											)}
										{(message.mediaUrl ||
											message.mediaType === "location" ||
											message.mediaType === "vcard" ||
											message.mediaType === "multi_vcard") &&
											message.mediaType !== "sticker" &&
											checkMessageMedia(message)}
										<Box
											className={
												message.mediaType !== "email"
													? clsx(classes.textContentItem, {
															[classes.textContentItemDeleted]: message.isDeleted,
													  })
													: undefined
											}
										>
											{message.isDeleted && (
												<Block
													color="disabled"
													fontSize="small"
													className={classes.deletedIcon}
												/>
											)}
											{message.quotedMsg && (
												<QuotedMessage
													message={message}
													searchedMessage={searchedMessage}
													searchLoading={searchLoading}
													getAroundMessages={getAroundMessages}
												/>
											)}

											{message && message.mediaType === "email" ? (
												<EmailMessageCard message={message} />
											) : (
												message &&
												message.mediaType === "chat" && (
													<MarkdownWrapper>{message.body}</MarkdownWrapper>
												)
											)}

											{message.isEdited && !message.isDeleted && (
												<span
													style={{
														display: "block",
														fontSize: 11,
														opacity: 0.7,
														fontStyle: "italic",
														marginTop: 2,
													}}
												>
													{i18n.t("messageOptionsMenu.edited")}
												</span>
											)}

											{renderInlineMessageReaction(message)}

											<span className={classes.timestamp}>
												{formatMessageClock(message.createdAt, businessTimeZone)}
											</span>
										</Box>
										{renderBubbleMessageReaction(message)}
									</div>


								)}
								{tailwickLayout ? (
									renderTailwickReactionActions(message)
								) : (
									<>
										<IconButton
											disableRipple
											onClick={() => handleReaction(message.id)}
											style={{ marginLeft: "-20px", opacity: "0.3" }}
										>
											<SentimentSatisfiedIcon />
										</IconButton>
										{reactionMessageId === message.id && (
											<ReactionsMsg
												activeEmoji={message.emoji}
												onSelect={(emoji) => {
													ReagirMsg(message.id, emoji);
													setReactionMessageId(null);
												}}
											/>
										)}
									</>
								)}
							</Box>
						</React.Fragment>
					);
				}
				return (
					<React.Fragment key={message.id}>
						{renderDailyTimestamps(message, index)}
						{renderMessageDivider(message, index)}
						{renderNumberTicket(message, index)}

						<Box
							display="flex"
							alignItems="center"
							width="100%"
							justifyContent="space-between"
							sx={{
								overflowWrap: "break-word",
								wordBreak: "break-word",
								whiteSpace: "normal"
							}}
						>
							{selectedMessages.length > 0 ? (
								<Checkbox
									onClick={() => handleSelectedMessages(message.id)}
									checked={selectedMessages.includes(message.id)}
								/>
							) : (
								<span></span>
							)}

							<div
								className={[
									darkMode ? classes.messageRightDark : classes.messageRight,
									message.mediaType === "sticker" ? classes.stickerNoBubble : "",
								]
									.filter(Boolean)
									.join(" ")}
								style={
									message.mediaType === "email"
										? {
											padding: 0,
											margin: "0.5em 0 0",
										}
										: null
								}
								id={message.id}
							>
								{message.mediaType !== "email" && (
									<IconButton
										variant="contained"
										size="small"
										id="messageActionsButton"
										disabled={message.isDeleted}
										className={classes.messageActionsButton}
										onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
									>
										<ExpandMore />
									</IconButton>
								)}

								{(message.mediaUrl ||
									message.mediaType === "location" ||
									message.mediaType === "vcard" ||
									message.mediaType === "multi_vcard") &&
									checkMessageMedia(message)}

								{message.mediaType === "sticker" ? (
									<span className={classes.timestamp}>
										{formatMessageClock(message.createdAt, businessTimeZone)}
										{renderMessageAck(message)}
									</span>
								) : message.mediaType !== "email" ? (
									<div
										className={clsx(classes.textContentItem, {
											[classes.textContentItemDeleted]: message.isDeleted,
										})}
									>
										{message.isDeleted && (
											<Block
												color="disabled"
												fontSize="small"
												className={classes.deletedIcon}
											/>
										)}
										{message.quotedMsg && (
											<QuotedMessage
												message={message}
												searchedMessage={searchedMessage}
												searchLoading={searchLoading}
												getAroundMessages={getAroundMessages}
											/>
										)}

										{
											(message.mediaType === "chat" ||
												message.mediaType === 'template') && (
												<MarkdownWrapper>{message.body}</MarkdownWrapper>
											)
										}

										{message.isEdited && !message.isDeleted && (
											<span
												style={{
													display: "block",
													fontSize: 11,
													opacity: 0.7,
													fontStyle: "italic",
													marginTop: 2,
												}}
											>
												{i18n.t("messageOptionsMenu.edited")}
											</span>
										)}

										{renderInlineMessageReaction(message)}

										<span className={classes.timestamp}>
											{formatMessageClock(message.createdAt, businessTimeZone)}
											{renderMessageAck(message)}
										</span>
									</div>
								) : (
									<Box
										className={
											message.mediaType !== "email" && classes.textContentItem
										}
									>
										{message.quotedMsg && (
											<QuotedMessage
												message={message}
												searchedMessage={searchedMessage}
												searchLoading={searchLoading}
												getAroundMessages={getAroundMessages}
											/>
										)}
										{message && message.mediaType === "email" ? (
											<EmailMessageCard message={message} />
										) : (
											<MarkdownWrapper>{message.body}</MarkdownWrapper>
										)}
										{renderInlineMessageReaction(message)}

										<span className={classes.timestamp}>
											{formatMessageClock(message.createdAt, businessTimeZone)}
										</span>
									</Box>
								)}
								{renderBubbleMessageReaction(message)}
							</div>
						</Box>
					</React.Fragment>
				);
			});

			return (
				<>
					{loading && (
						<div>
							<CircularProgress className={classes.circleLoading} />
						</div>
					)}
					{viewMessagesList}
				</>
			);
		}
		return <div>Say hello to your new contact!</div>;
	};

	const resolveFileMediaUrl = (mediaUrl) => resolveMediaUrl(mediaUrl);

	const getFileExtension = (filename) => {
		if (filename == null || filename === "") return "—";
		const name = String(filename);
		// slice filename from the last dot
		const extension = name.slice(
			((name.lastIndexOf(".") - 1) >>> 0) + 2
		);

		return extension || "—";
	};

	const getFileSize = (mediaUrl) => {
		const resolved = resolveFileMediaUrl(mediaUrl);
		if (!resolved) return "—";
		const xhr = new XMLHttpRequest();
		xhr.open("HEAD", resolved, false);
		xhr.send();

		const fileSize = xhr.getResponseHeader("content-length");

		if (fileSize >= 1048576) {
			return `${(fileSize / 1048576).toFixed(2)} MB`;
		}
		if (fileSize >= 1024) {
			return `${(fileSize / 1024).toFixed(2)} KB`;
		}
		if (fileSize > 1) {
			return `${fileSize} B`;
		}
		return `${fileSize} B`;
	};

	useEffect(() => {
		scrollToMessage();
	}, [scrollToMessage]);

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
		setHasMore(false);
		loadingMoreRef.current = false;
		scrollPreserveRef.current = null;
		aroundModeRef.current = false;
		pinToBottomAfterLoadRef.current = true;
		autoFillPagesRef.current = 0;
		// Evita IntersectionObserver/loadMore no topo antes do pin no final.
		suppressLoadMoreUntilRef.current = Date.now() + 1500;
		setMessagePrivate?.(false);

		currentTicketId.current = ticketId;
	}, [ticketId, privateAnnotationsOnly, setMessagePrivate]);

	useEffect(() => {
		aroundModeRef.current = Boolean(aroundMessages?.length);
		if (aroundMessages?.length > 0) {
			dispatch({ type: "RESET" });
			dispatch({
				type: "LOAD_MESSAGES",
				payload: filterMessagesForOpenTicket(
					aroundMessages,
					ticketId,
					ticketIsStatus
				),
			});
			setHasMore(false);
			loadingMoreRef.current = false;
		}
	}, [aroundMessages, ticketId, ticketIsStatus]);

	useEffect(() => {
		const onAnnotationCreated = (event) => {
			const { ticketId: eventTicketId, message } = event.detail || {};
			if (String(eventTicketId) !== String(ticketId) || !message?.id) {
				return;
			}
			if (!isAnnotationMessage(message)) {
				return;
			}
			dispatch({ type: "ADD_MESSAGE", payload: message });
			scrollToBottom("smooth");
		};

		window.addEventListener(TICKET_ANNOTATION_CREATED, onAnnotationCreated);
		return () => {
			window.removeEventListener(TICKET_ANNOTATION_CREATED, onAnnotationCreated);
		};
	}, [ticketId]);

	useEffect(() => {
		if (!forwardModalOpen) {
			setSelectedMessages([]);
		}
	}, [forwardModalOpen]);

	useEffect(() => {
		if (privateAnnotationsOnly) {
			return undefined;
		}
		// Enquanto estiver no recorte "around", não mistura com a paginação normal.
		if (aroundModeRef.current) {
			return undefined;
		}

		let cancelled = false;
		setLoading(true);
		const fetchMessages = async () => {
			const requestedPage = pageNumber;
			const requestedTicketId = ticketId;
			try {
				const { data } = await apiV2.get(`/messages/${ticketId}`, {
					params: { pageNumber: requestedPage },
				});
				if (cancelled) return;
				if (currentTicketId.current !== requestedTicketId || aroundModeRef.current) {
					return;
				}

				const rawMessages = Array.isArray(data.messages) ? data.messages : [];

				// Página além do fim (ex.: count 380 + messages []) — só encerra paginação.
				if (rawMessages.length === 0) {
					setHasMore(false);
					hasMoreRef.current = false;
					return;
				}

				const messages = filterMessagesForOpenTicket(
					sortByKey(rawMessages, "createdAt"),
					ticketId,
					ticketIsStatus
				);
				dispatch({ type: "LOAD_MESSAGES", payload: messages });
				const nextHasMore = Boolean(data.hasMore);
				setHasMore(nextHasMore);
				hasMoreRef.current = nextHasMore;

				if (requestedPage > 1 && scrollPreserveRef.current) {
					const container = document.getElementById("messagesList");
					const preserved = scrollPreserveRef.current;
					scrollPreserveRef.current = null;
					if (container) {
						// Evita o restore disparar outro loadMore no mesmo frame.
						suppressLoadMoreUntilRef.current = Date.now() + 200;
						requestAnimationFrame(() => {
							const delta = container.scrollHeight - preserved.height;
							container.scrollTop = preserved.top + delta;
						});
					}
				} else if (
					requestedPage === 1 ||
					pinToBottomAfterLoadRef.current
				) {
					suppressLoadMoreUntilRef.current = Date.now() + 1200;
					scrollToBottom("instant");
				}

				// Página veio da API mas, após filtro, não sobrou nada visível:
				// tenta no máximo mais algumas páginas (mesmo teto do auto-fill).
				if (
					Boolean(data.hasMore) &&
					messages.length === 0 &&
					autoFillPagesRef.current < MAX_AUTO_FILL_PAGES
				) {
					autoFillPagesRef.current += 1;
					loadingMoreRef.current = false;
					setLoading(false);
					setPageNumber((prev) => prev + 1);
					return;
				}
			} catch (err) {
				if (!cancelled) toastError(err);
			} finally {
				// Só libera se esta request ainda é a vigente. No cleanup de
				// troca de pageNumber NÃO zerar o lock — senão scroll rápido
				// dispara duas páginas seguidas.
				if (!cancelled) {
					loadingMoreRef.current = false;
					setLoading(false);
				}
			}
		};

		fetchMessages();
		return () => {
			cancelled = true;
		};
	}, [pageNumber, ticketId, ticketIsStatus, privateAnnotationsOnly]);

	// Continua pedindo páginas se ainda houver histórico e a lista não
	// preencher a tela (sem scroll o onScroll nunca dispara).
	useEffect(() => {
		if (privateAnnotationsOnly || loading || aroundModeRef.current) return undefined;
		if (!hasMore) return undefined;

		const timer = window.setTimeout(() => {
			maybeLoadOlderIfNeeded();
		}, 120);

		return () => window.clearTimeout(timer);
	}, [
		chatMessages.length,
		hasMore,
		loading,
		privateAnnotationsOnly,
		maybeLoadOlderIfNeeded,
		ticketId,
		pageNumber,
	]);

	useEffect(() => {
		hasMoreRef.current = hasMore;
	}, [hasMore]);

	useEffect(() => {
		const sentinel = topSentinelRef.current;
		const root = document.getElementById("messagesList");
		if (!sentinel || !root) return undefined;

		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (!entry?.isIntersecting) return;
				if (aroundModeRef.current || aroundMessages?.length) return;
				if (!hasMoreRef.current || loadingMoreRef.current) return;
				if (Date.now() < suppressLoadMoreUntilRef.current) return;
				loadMore();
			},
			{ root, rootMargin: "80px 0px 0px 0px", threshold: 0 }
		);

		observer.observe(sentinel);
		return () => observer.disconnect();
		// Observer estável: lê hasMore/loading via refs (evita recriar e disparar 2x).
	}, [loadMore, aroundMessages, ticketId]);

	// Só marca o indicador "tem anotação privada" — não mexe na lista nem no hasMore.
	useEffect(() => {
		if (privateAnnotationsOnly || typeof setMessagePrivate !== "function") {
			return undefined;
		}

		let cancelled = false;
		const checkPrivateAnnotations = async () => {
			try {
				const { data } = await api.get(
					`/tickets/${ticketId}/private-annotations`
				);
				if (cancelled || currentTicketId.current !== ticketId) return;
				const list = Array.isArray(data?.messages)
					? data.messages
					: Array.isArray(data)
						? data
						: [];
				setMessagePrivate(list.length > 0);
			} catch {
				if (!cancelled) setMessagePrivate(false);
			}
		};

		checkPrivateAnnotations();
		return () => {
			cancelled = true;
		};
	}, [ticketId, privateAnnotationsOnly, setMessagePrivate]);

	useEffect(() => {
		if (!privateAnnotationsOnly) {
			return undefined;
		}
		setLoading(true);
		const fetchPrivateAnnotations = async () => {
			try {
				const { data } = await api.get(
					`/tickets/${ticketId}/private-annotations`
				);
				if (currentTicketId.current !== ticketId) {
					return;
				}
				const list = Array.isArray(data?.messages)
					? data.messages
					: Array.isArray(data)
						? data
						: [];
				dispatch({
					type: "LOAD_MESSAGES",
					payload: sortMessagesByCreatedAt(list),
				});
				setHasMore(false);
				if (list.length > 0) {
					scrollToBottom("instant");
					setMessagePrivate?.(true);
				} else {
					setMessagePrivate?.(false);
				}
			} catch (err) {
				toastError(err);
			} finally {
				setLoading(false);
			}
		};

		fetchPrivateAnnotations();
		return undefined;
	}, [ticketId, privateAnnotationsOnly, setMessagePrivate]);

	useEffect(() => {
		const socket = openSocket({
			scope: "messageslist",
			userId: user.id,
			component: "MessagesList",
			companyId: user.companyId ?? "",
		});

		socket.on("connect", () => {
			socket.emit("joinChatBox", ticketId);
		});

		socket.on("appMessage", (data) => {
			if (data.action === "create") {
				if (privateAnnotationsOnly) {
					if (!isAnnotationMessage(data.message)) return;
					dispatch({ type: "ADD_MESSAGE", payload: data.message });
					scrollToBottom("smooth");
					return;
				}
				if (!messageBelongsToTicketChat(data.message)) return;
				dispatch({ type: "ADD_MESSAGE", payload: data.message });
				scrollToBottom("smooth");
			}

			if (data.action === "update") {
				if (!data.message) return;
				dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [ticketId, messageBelongsToTicketChat, user.id, user.companyId, privateAnnotationsOnly]);


	//socket V2
	useEffect(() => {
		if (!user?.id) return;

		const handleMessage = (event) => {
			let data;
			try {
				data = JSON.parse(event.data);
			} catch {
				return;
			}

			if (!data.message) {
				return;
			}
			if (data.action === "create") {
				if (privateAnnotationsOnly) {
					if (!isAnnotationMessage(data.message)) return;
					dispatch({ type: "ADD_MESSAGE", payload: data.message });
					scrollToBottom("smooth");
					return;
				}
				if (data.ticket?.id != ticketId) return;
				if (!messageBelongsToTicketChat(data.message)) return;

				dispatch({
					type: "ADD_MESSAGE",
					payload: data.message,
				});

				scrollToBottom("smooth");
			}

			if (data.action === "update") {
				const msg = data.message;
				if (
					msg?.ticketId != null &&
					String(msg.ticketId) !== String(ticketId)
				) {
					return;
				}
				dispatch({
					type: "UPDATE_MESSAGE",
					payload: msg,
					tempId: data.tempId,
				});
			}
		};

		const joinRoom = () => {
			const s = getGoWebSocket();
			if (!s || s.readyState !== WebSocket.OPEN) return;
			s.send(
				JSON.stringify({
					action: "joinChatBox",
					ticketId: Number(ticketId),
				})
			);
		};

		const removeMessage = addGoWebSocketMessageHandler(handleMessage);
		const removeOpen = addGoWebSocketOpenHandler(joinRoom);
		connectSocket({
			userId: user.id,
			companyId: user.companyId ?? 0,
		});

		return () => {
			removeMessage();
			removeOpen();
			const s = getGoWebSocket();
			if (s && s.readyState === WebSocket.OPEN) {
				s.send(
					JSON.stringify({
						action: "leaveChatBox",
						ticketId: Number(ticketId),
					})
				);
			}
		};
	}, [ticketId, user?.id, messageBelongsToTicketChat, privateAnnotationsOnly]);


	const translateMensagem = async (message) => {
		let response = await api.post("/translate", {
			text: message.body,
			linguagem: "pt_br"
		})

		dispatch({
			type: "UPDATE_MESSAGE",
			payload: {
				id: message.id,
				body: response.data
			}
		});
	}

	const officialWhatsapp =
		isOfficialWhatsappChannel(ticketChannel) ||
		isOfficialWhatsappChannel(connectionChannel);

	useEffect(() => {
		if (privateAnnotationsOnly || !setTemplateBarStatus) return;
		if (!officialWhatsapp) {
			setTemplateBarStatus(false);
			return;
		}
		if (loading && chatMessages.length === 0) return;
		setTemplateBarStatus(isOutsideMetaWindow(chatMessages, businessTimeZone));
	}, [
		businessTimeZone,
		chatMessages,
		loading,
		officialWhatsapp,
		privateAnnotationsOnly,
		setTemplateBarStatus,
	]);

	return (
		<div className={clsx(classes.messagesListWrapper, tailwickLayout && "tailwick-messages-list")}>
			{!privateAnnotationsOnly && officialWhatsapp && templateBarStatus && (
				<TemplateOnlyBar />
			)}
			{(["whatsappbusiness", "whatsapp_business_account","whatsapp"].includes(ticketChannel)) &&
				selectedMessages.length > 0 && (
					<Box className={classes.forwardMenu}>
						<Box display="flex" alignItems="center" style={{ gap: "1rem" }}>
							<IconButton onClick={() => setSelectedMessages([])}>
								<Close />
							</IconButton>
							<Typography>
								{`${selectedMessages.length} ${selectedMessages.length === 1 ? "selecionada" : "selecionadas"
									}`}
							</Typography>
						</Box>
						<Box display="flex" style={{ gap: "1rem" }}>
							{/* <IconButton>
							<Delete />
						</IconButton> */}
							<IconButton onClick={() => setForwardModalOpen(true)}>
								<Reply
									style={{
										// invert x axis
										transform: "scaleX(-1)",
									}}
								/>
							</IconButton>
						</Box>
					</Box>
				)}

			<MessageOptionsMenu
				message={selectedMessage}
				selectedMessages={selectedMessages}
				setSelectedMessages={setSelectedMessages}
				anchorEl={anchorEl}
				menuOpen={messageOptionsMenuOpen}
				handleClose={handleCloseMessageOptionsMenu}
				translateMensagem={translateMensagem}
				tailwickLayout={tailwickLayout}
				ticketChannel={ticketChannel}
			/>

			<ForwardMessagesModal
				modalOpen={forwardModalOpen}
				onClose={() => setForwardModalOpen(false)}
				selectedMessages={selectedMessages}
				whatsappId={whatsappId}
			/>

			<div
				id="messagesList"
				className={classes.messagesList}
				onScroll={handleScroll}
				style={
					selectedMessages.length ? { paddingBottom: "3.5rem" } : undefined
				}
			>
				<div ref={topSentinelRef} aria-hidden style={{ height: 1, width: "100%" }} />
				{loading && pageNumber > 1 && (
					<div style={{ display: "flex", justifyContent: "center", padding: 8 }}>
						<CircularProgress size={20} style={{ color: "#3b82f6" }} />
					</div>
				)}
				{privateAnnotationsOnly && !loading && !chatMessages.length ? (
					<div className="tailwick-private-messages-history__empty">
						<Lock fontSize="small" aria-hidden />
						<p>{i18n.t("messagesInput.privateMessageHistoryEmpty")}</p>
					</div>
				) : chatMessages.length ? (
					renderMessages()
				) : null}
			</div>

			{(buttonScrollDown || Boolean(aroundMessages?.length)) && (
				<IconButton
					className={clsx(
						tailwickLayout
							? "tailwick-scroll-down-btn"
							: clsx(classes.scrollDownButton, darkMode && classes.scrollDownButtonDark)
					)}
					onClick={async () => {
						aroundModeRef.current = false;
						setPageNumber(1);
						setAroundMessages([]);
						loadingMoreRef.current = false;
						try {
							const { data } = await apiV2.get(`/messages/${ticketId}`, {
								params: { pageNumber: 1 },
							});

							if (currentTicketId.current === ticketId) {
								dispatch({ type: "RESET" });
								dispatch({
									type: "LOAD_MESSAGES",
									payload: filterMessagesForOpenTicket(
										sortByKey(data.messages, "createdAt"),
										ticketId,
										ticketIsStatus
									),
								});
								setHasMore(data.hasMore);
								setLoading(false);
							}
						} catch (err) {
							setLoading(false);
							toastError(err);
						}

						scrollToBottom('smooth');
					}}
				>
					<KeyboardArrowDownIcon />
				</IconButton>
			)}
		</div>
	);
}

export default MessagesList;
